import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Images } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { TID } from "@/constants/testIds";
import { VoiceConfirmModal } from "@/components/add/VoiceConfirmModal";

export function PhotoCapture({ autoStart = false, onDone }) {
  const { t, lang } = useI18n();
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [thumb, setThumb] = useState(null);
  const [preview, setPreview] = useState(null); // { extracted }
  useEffect(() => { if (autoStart) setTimeout(() => camRef.current?.click(), 120); }, [autoStart]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setThumb(URL.createObjectURL(file));
    setBusy(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const { data } = await api.post("/ai/photo?preview=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const items = data.extracted || [];
      if (items.length === 0) toast.info(lang === "ar" ? "لم يتم استخراج أي عناصر" : "No items detected");
      else setPreview({ extracted: items });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <input data-testid={TID.photoInput} ref={camRef} type="file" accept="image/*"
        capture="environment" className="hidden" onChange={onFile} />
      <input data-testid="photo-gallery-input" ref={galRef} type="file" accept="image/*"
        className="hidden" onChange={onFile} />
      <button data-testid={TID.photoButton} onClick={() => camRef.current?.click()} disabled={busy}
        className="w-40 h-40 rounded-full neu-fill-blue text-white grid place-items-center active:scale-95 transition-all shadow-2xl shadow-blue-900/40 disabled:opacity-70">
        {busy ? <Loader2 className="w-14 h-14 animate-spin" /> : <Camera className="w-14 h-14" />}
      </button>
      <p className="text-sm text-slate-600 text-center max-w-sm">{busy ? t("processing") : t("snap")}</p>
      <button data-testid="photo-gal-btn" onClick={() => galRef.current?.click()} disabled={busy}
        className="h-12 px-6 rounded-2xl neu-btn font-semibold flex items-center justify-center gap-2 neu-accent-blue disabled:opacity-60">
        <Images className="w-4 h-4" />{lang === "ar" ? "اختيار من المعرض" : "Choose from gallery"}
      </button>
      {thumb && <img src={thumb} alt="preview" className="max-h-40 rounded-2xl neu-sm" />}
      <VoiceConfirmModal
        open={!!preview}
        transcript={lang === "ar" ? "فاتورة" : "Receipt"}
        extracted={preview?.extracted}
        onClose={() => { setPreview(null); onDone?.(); }}
        onSaved={() => { setPreview(null); onDone?.(); }}
      />
    </div>
  );
}
